import { useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Upload, CheckCircle, AlertTriangle, FileSpreadsheet, Loader2, Server, DoorOpen, Download } from 'lucide-react';

const CSV_TEMPLATE = `Device Name;Device EUI;App Key;Join EUI;Room Name
Sensor Toalett Plan 1;A81758FFFE060001;00112233445566778899AABBCCDDEEFF;0000000000000001;Toalett Plan 1
Sensor Toalett Plan 2;A81758FFFE060002;00112233445566778899AABBCCDDEEFF;0000000000000001;Toalett Plan 2
Sensor Omklädning;A81758FFFE060003;AABBCCDDEEFF00112233445566778899;0000000000000001;Omklädningsrum pojkar`;

export default function ImportSensorsPage() {
  const [file, setFile] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vapelarm_import_mall.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleFile = (f) => {
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.txt'))) {
      setFile(f);
      setResult(null);
    } else {
      toast.error('Välj en CSV-fil');
    }
  };

  const handleUpload = async () => {
    if (!file || !schoolName.trim()) {
      toast.error('Fyll i skolnamn och välj en CSV-fil');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('school_name', schoolName.trim());
      const res = await api.post('/import/sensors', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(`${res.data.devices_created} sensorer importerade!`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Import misslyckades';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="import-sensors-page">
      <div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
          Importera sensorer
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm">
          Flytta sensorer från ett annat system via CSV-fil
        </p>
      </div>

      {/* Instructions */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-chivo font-bold text-sm text-blue-800 dark:text-blue-300 mb-2">CSV-format</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 font-ibm mb-3">
                CSV-filen ska ha dessa kolumner (semikolon-separerad):
              </p>
              <code className="text-xs bg-white dark:bg-slate-800 px-3 py-2 rounded border border-slate-200 dark:border-slate-700 block font-mono text-slate-700 dark:text-slate-300">
                Device Name; Device EUI; App Key; Join EUI; Room Name
              </code>
              <p className="text-xs text-slate-500 mt-2 font-ibm">
                Rummen skapas automatiskt. Dubbletter (samma Device EUI) hoppas över.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="shrink-0 gap-2 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30"
              data-testid="download-template-btn"
            >
              <Download className="h-4 w-4" />
              Ladda ner mall
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Upload area */}
        <Card>
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Ladda upp</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1.5">
                Skolnamn
              </label>
              <Input
                value={schoolName}
                onChange={e => setSchoolName(e.target.value)}
                placeholder="T.ex. Fröviskolan"
                data-testid="school-name-input"
              />
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${dragOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}
                ${file ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => inputRef.current?.click()}
              data-testid="csv-dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={e => handleFile(e.target.files[0])}
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="h-10 w-10 text-emerald-500" />
                  <p className="text-sm font-ibm font-semibold text-emerald-700 dark:text-emerald-400">{file.name}</p>
                  <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                  <p className="text-sm text-slate-500 font-ibm">Dra och släpp CSV-fil här</p>
                  <p className="text-xs text-slate-400">eller klicka för att välja</p>
                </div>
              )}
            </div>

            <Button
              onClick={handleUpload}
              disabled={!file || !schoolName.trim() || uploading}
              className="w-full gap-2"
              data-testid="import-submit-btn"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? 'Importerar...' : 'Importera sensorer'}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className={result ? '' : 'opacity-50'}>
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Resultat</CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg" data-testid="devices-created-stat">
                    <p className="text-2xl font-chivo font-black text-emerald-600">{result.devices_created}</p>
                    <p className="text-xs text-slate-500 font-ibm">Sensorer skapade</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-testid="rooms-created-stat">
                    <p className="text-2xl font-chivo font-black text-blue-600">{result.rooms_created}</p>
                    <p className="text-xs text-slate-500 font-ibm">Rum skapade</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <p className="text-2xl font-chivo font-black text-amber-600">{result.devices_skipped}</p>
                    <p className="text-xs text-slate-500 font-ibm">Överhoppade</p>
                  </div>
                </div>

                {/* School info */}
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 font-ibm">
                  <Server className="h-4 w-4 text-slate-400" />
                  Skola: <span className="font-semibold">{result.school?.name}</span>
                </div>

                {/* Device list */}
                {result.devices?.length > 0 && (
                  <div className="max-h-60 overflow-y-auto space-y-1.5">
                    {result.devices.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs font-ibm bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">{d.name}</span>
                        <span className="text-slate-400 truncate">{d.eui}</span>
                        <span className="text-slate-400 ml-auto flex items-center gap-1 shrink-0">
                          <DoorOpen className="h-3 w-3" /> {d.room}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Errors */}
                {result.errors?.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="text-xs font-ibm font-semibold text-amber-700 dark:text-amber-400">Varningar:</h4>
                    {result.errors.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 font-ibm">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {e}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileSpreadsheet className="h-12 w-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-sm text-slate-400 font-ibm">Importera en CSV-fil för att se resultat</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
